import { memo, useCallback, useRef, useState } from "react";

import { MAGAZINE_MEDIA_PUBLICATION_REQUEST_MUTATION } from "@/lib/apollo-client/magazine-media-publication-request-mutation";
import { MAGAZINE_MEDIA_PUBLICATION_STATUS_QUERY } from "@/lib/apollo-client/magazine-media-publication-status-query";

import { useCustomMutation, useCustomSuspenseQuery } from "@/hooks/use-graphql-client";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { Button } from "@/components/ui/button/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog/dialog";

type Props = {
	hotelID: string;
};

export const MEDIA_PAGE_URL = "https://www.chillnn.com/ja/?inflowfrom=MAGAZINE_MEDIA";

const NOTES = [
	"掲載の可否および掲載位置は、編集部による選考のうえ決定いたします。選考結果・理由・掲載時期については、個別のご連絡をいたしかねますので、あらかじめご了承ください。",
	"ご入力いただいた内容がそのまま掲載されます。マガジン記事に掲載する文面や画像等をご入力のうえ、申請してください。",
];

const Notes = () => (
	<div className="space-y-1 text-sm text-zinc-500">
		<p>【ご注意事項】</p>
		<ul className="space-y-1">
			{NOTES.map((note) => (
				<li key={note}>・{note}</li>
			))}
		</ul>
	</div>
);

export const MagazineMediaPublicationRequest = memo(({ hotelID }: Props) => {
	// 依頼は施設につき1回きりなので、送信済みかどうかはサーバーのステータスを正とする
	const { data: statusData } = useCustomSuspenseQuery(MAGAZINE_MEDIA_PUBLICATION_STATUS_QUERY, {
		variables: { hotelID },
	});
	const [justRequested, setJustRequested] = useState(false);
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const requested = justRequested || !!statusData?.hotelApplicationStatus.isMagazineMediaPublicationRequested;
	const submittingRef = useRef(false);
	const { mutationFunc, mutationResult } = useCustomMutation(MAGAZINE_MEDIA_PUBLICATION_REQUEST_MUTATION);

	const handleConfirm = useCallback(async () => {
		if (requested || submittingRef.current) {
			return;
		}

		submittingRef.current = true;
		try {
			const { data, errors } = await mutationFunc({ variables: { input: { hotelID } } });
			// GraphQL / 通信エラーは useCustomMutation 側で通知する
			if (errors) {
				return;
			}

			const payload = data?.magazineMediaPublicationRequest;
			const userError = payload?.userErrors[0];
			if (!payload || userError) {
				notifyUserEventInfo({
					variant: "error",
					title: userError ?? "掲載依頼を送信できませんでした",
					description: "時間をおいて再度お試しください。",
				});
				return;
			}

			setJustRequested(true);
			notifyUserEventInfo({
				variant: "default",
				title: "掲載依頼を送信しました",
			});
		} finally {
			submittingRef.current = false;
			setIsConfirmOpen(false);
		}
	}, [hotelID, mutationFunc, requested]);

	return (
		<div className="space-y-4">
			<p className="text-sm text-zinc-500">
				<a
					href={MEDIA_PAGE_URL}
					target="_blank"
					rel="noopener noreferrer"
					className="text-link-400 underline underline-offset-2"
				>
					CHILLNNギャラリーサイト
				</a>
				への掲載をご希望の場合は、マガジン記事をご入力のうえ、下記ボタンから申請してください。
			</p>
			<Notes />
			<Button
				type="button"
				variant={requested ? "disabled" : "outline"}
				disabled={requested}
				onClick={() => setIsConfirmOpen(true)}
			>
				{requested ? "掲載依頼済み" : "CHILLNNギャラリーサイトに掲載を申請する"}
			</Button>

			<Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>CHILLNNギャラリーサイトへの掲載を申請しますか？</DialogTitle>
						<DialogDescription>申請は施設につき1回のみです。以下をご確認ください。</DialogDescription>
					</DialogHeader>
					<Notes />
					<DialogFooter>
						<Button type="button" variant="outline" onClick={() => setIsConfirmOpen(false)}>
							キャンセル
						</Button>
						<Button type="button" loading={mutationResult.loading} onClick={handleConfirm}>
							申請する
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
});

MagazineMediaPublicationRequest.displayName = "MagazineMediaPublicationRequest";
